import { forwardRef, useRef } from 'react';
import type {
  CSSProperties,
  InputHTMLAttributes,
  KeyboardEvent,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/cn';
import { PAGE_FILTER_KEY, usePageFilterShortcut } from '../../lib/keyboard';
import { CalendarInput } from './CalendarInput';
import { useFieldControl } from './Field';
import { Icon } from './Icon';
import { ReadOnlyProvider, useIsReadOnly } from './ReadOnly';

/** `sm`: 32px filter-bar controls. `md`: 36px form controls in drawers and dialogs. */
export type ControlSize = 'sm' | 'md';

const CONTROL =
  'rounded-2 border bg-bg-3 text-[13px] text-fg-1 disabled:cursor-not-allowed disabled:opacity-40 ' +
  'aria-[invalid=true]:border-danger';
const HEIGHT: Record<ControlSize, string> = { sm: 'h-8', md: 'h-9' };

interface ControlA11y {
  readonly id?: string;
  readonly 'aria-describedby'?: string;
  readonly 'aria-invalid'?: boolean | 'true' | 'false' | 'grammar' | 'spelling';
  readonly disabled?: boolean;
}

/**
 * Merges Field context (id, describedby, invalid) with explicit props; explicit props win. A
 * control in a closed month is disabled (V206).
 */
function useControlA11y(props: ControlA11y) {
  const field = useFieldControl();
  const isReadOnly = useIsReadOnly();
  return {
    disabled: props.disabled || isReadOnly || undefined,
    id: props.id ?? field?.id,
    'aria-describedby': props['aria-describedby'] ?? field?.describedBy,
    'aria-invalid': props['aria-invalid'] ?? (field?.invalid ? true : undefined),
  };
}

const borderClass = 'border-border-1';

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  readonly size?: ControlSize;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { size = 'md', className, type = 'text', ...rest },
  ref,
) {
  const a11y = useControlA11y(rest);
  return (
    <input
      ref={ref}
      type={type}
      {...rest}
      {...a11y}
      className={cn(CONTROL, borderClass, HEIGHT[size], 'px-2.5', className)}
    />
  );
});

/** Date input styled like the text inputs (value `YYYY-MM-DD`) with the app's calendar (V186). */
export const DateInput = forwardRef<HTMLInputElement, Omit<TextInputProps, 'type'>>(function DateInput(
  { size = 'md', className, ...rest },
  ref,
) {
  const a11y = useControlA11y(rest);
  return (
    <CalendarInput
      ref={ref}
      type="date"
      {...rest}
      {...a11y}
      className={cn(CONTROL, borderClass, HEIGHT[size], 'px-2.5', className)}
    />
  );
});

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  readonly size?: ControlSize;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { size = 'md', className, children, ...rest },
  ref,
) {
  const a11y = useControlA11y(rest);
  return (
    <select
      ref={ref}
      {...rest}
      {...a11y}
      className={cn(CONTROL, borderClass, HEIGHT[size], 'px-2.5', className)}
    >
      {children}
    </select>
  );
});

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, rows = 2, ...rest },
  ref,
) {
  const a11y = useControlA11y(rest);
  return (
    <textarea
      ref={ref}
      rows={rows}
      {...rest}
      {...a11y}
      className={cn(CONTROL, borderClass, 'resize-y px-2.5 py-2', className)}
    />
  );
});

export interface FilterInputProps extends Omit<TextInputProps, 'size'> {
  /** Icon inside the left edge (e.g. `list-filter`, 14px, fg-3). */
  readonly icon?: LucideIcon;
  /**
   * Key cap inside the right edge. `"/"` also registers the page shortcut: pressing "/" outside
   * a text field focuses this input (usePageFilterShortcut).
   */
  readonly shortcut?: ReactNode;
  /** Width of the control in px (design: 220–260). */
  readonly width?: number;
  /**
   * Lets the box shrink to this width (px) on narrow screens before its flex row wraps; `width`
   * stays its maximum, so wide screens are unchanged.
   */
  readonly minWidth?: number;
}

function filterBoxStyle(width: number, minWidth: number | undefined): CSSProperties {
  return minWidth === undefined ? { width } : { flex: `1 1 ${minWidth}px`, minWidth, maxWidth: width };
}

/**
 * 32px filter-bar search box with optional icon and shortcut key cap. Esc or Enter leaves the
 * field (design MC:1714) so the grid keys work again.
 */
export const FilterInput = forwardRef<HTMLInputElement, FilterInputProps>(function FilterInput(
  { icon, shortcut, width = 240, minWidth, className, onKeyDown, ...rest },
  ref,
) {
  const innerRef = useRef<HTMLInputElement | null>(null);
  usePageFilterShortcut(innerRef, shortcut === PAGE_FILTER_KEY);
  const setRefs = (element: HTMLInputElement | null) => {
    innerRef.current = element;
    if (typeof ref === 'function') ref(element);
    else if (ref) ref.current = element;
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (!event.defaultPrevented && (event.key === 'Escape' || event.key === 'Enter'))
      event.currentTarget.blur();
  };

  return (
    <div
      className={cn('relative', minWidth === undefined && 'flex-none')}
      style={filterBoxStyle(width, minWidth)}
    >
      {icon && (
        <span className="pointer-events-none absolute left-2.5 top-1/2 flex -translate-y-1/2 text-fg-3">
          <Icon icon={icon} size={14} />
        </span>
      )}
      {/* Filtering is reading: the search box stays usable in a closed month (V206). */}
      <ReadOnlyProvider isReadOnly={false}>
        <TextInput
          ref={setRefs}
          size="sm"
          enterKeyHint="search"
          aria-keyshortcuts={shortcut === PAGE_FILTER_KEY ? PAGE_FILTER_KEY : undefined}
          onKeyDown={handleKeyDown}
          className={cn(
            'w-full outline-pi-coral',
            icon ? 'pl-[30px]' : undefined,
            shortcut ? 'pr-[34px]' : undefined,
            className,
          )}
          {...rest}
        />
      </ReadOnlyProvider>
      {shortcut !== undefined && (
        <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded-[5px] border border-border-1 px-[5px] font-sans text-[11px] font-semibold leading-4 text-fg-3">
          {shortcut}
        </kbd>
      )}
    </div>
  );
});
